import * as React from 'react';
import { useEffect, useId, useRef, useState } from 'react';
import { Check, Minus } from '../Icon/icons';
import { Icon } from '../Icon/Icon';
import { outer, wrapper, nativeInput, control, box, labelText, helperText } from './Checkbox.css';

export type CheckboxSize = 'sm' | 'md';

export interface CheckboxProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'size' | 'type'
> {
  label?: React.ReactNode;
  helper?: string;
  /**
   * Error caption — its presence drives the alarm border + `aria-invalid`.
   * Uniform across the form family; there is no separate `invalid?` boolean
   * (L26).
   */
  error?: string;
  size?: CheckboxSize;
  /** Tri-state: when true, renders the dash glyph regardless of checked. */
  indeterminate?: boolean;
  /** React 19 ref-as-prop (replaces the old forwardRef pattern). */
  ref?: React.Ref<HTMLInputElement>;
}

/**
 * Checkbox — boolean toggle, two sizes, with tri-state support
 * (`indeterminate` renders the dash glyph regardless of `checked`).
 * Helper / error captions auto-associate via `aria-describedby`.
 * Supports controlled (`checked` + `onChange`) and uncontrolled
 * (`defaultChecked`) modes — same wiring as the native input.
 */
export function Checkbox({
  label,
  helper,
  error,
  size = 'md',
  indeterminate = false,
  id,
  disabled,
  checked,
  defaultChecked,
  onChange,
  className,
  style,
  ref,
  ...rest
}: CheckboxProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const helperId = `${inputId}-helper`;
  const localRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (localRef.current) localRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  const setRefs = (el: HTMLInputElement | null) => {
    localRef.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) (ref as { current: HTMLInputElement | null }).current = el;
  };

  // Track uncontrolled checked state so the styled box reacts to native
  // toggles. In controlled mode the `checked` prop is the single source of
  // truth; in uncontrolled mode we seed from `defaultChecked` and update on
  // every change (still forwarding the consumer's onChange). Without this the
  // styled box was frozen at its initial value (H1).
  const isControlled = checked !== undefined;
  const [uncontrolledChecked, setUncontrolledChecked] = useState(Boolean(defaultChecked));
  const isChecked = isControlled ? checked : uncontrolledChecked;

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    if (!isControlled) setUncontrolledChecked(e.currentTarget.checked);
    onChange?.(e);
  };

  const showCheck = indeterminate || isChecked;

  return (
    <div className={[outer, className].filter(Boolean).join(' ')} style={style}>
      <label className={wrapper({ disabled: Boolean(disabled) })} htmlFor={inputId}>
        <span className={control}>
          <input
            ref={setRefs}
            type="checkbox"
            id={inputId}
            checked={checked}
            defaultChecked={defaultChecked}
            disabled={disabled}
            onChange={handleChange}
            className={nativeInput}
            aria-invalid={Boolean(error) || undefined}
            aria-describedby={helper || error ? helperId : undefined}
            {...rest}
          />
          <span
            className={box({
              size,
              checked: Boolean(isChecked) || indeterminate,
              error: Boolean(error),
            })}
            aria-hidden="true"
          >
            {/* Glyphs route through the DS Icon wrapper (M10). The
                `& svg { stroke-width: 3 }` size-variant rule in Checkbox.css
                still wins (declared on the box, more specific than Icon's
                inline default), preserving the heavier check/dash weight. */}
            {showCheck && <Icon as={indeterminate ? Minus : Check} />}
          </span>
        </span>
        {label && <span className={labelText({ size })}>{label}</span>}
      </label>
      {(helper || error) && (
        <span id={helperId} className={helperText({ error: Boolean(error) })}>
          {error ?? helper}
        </span>
      )}
    </div>
  );
}
Checkbox.displayName = 'Checkbox';
